// Product variant grouping — derive a display "size" and a grouping "baseName" from a product name,
// and collapse same-base+price products into ONE card carrying its size/variant list.
//
// Sized items are separate Product rows (e.g. "Jacket - Nursing XSmall" vs "Jacket - Nursing Large").
// The shop grid AND the Featured Items section must both show one card per base product, not one per
// size (SV-6). This is the single shared implementation both use so they dedupe identically.

// Pure by name → memoize (previously ran 3 regexes per product up to 3× per render).
const _variantCache = new Map();

export function parseProductVariant(name) {
    name = name || '';
    if (_variantCache.has(name)) return _variantCache.get(name);

    // Only the SIZE is a variant to collapse — color stays part of the base (each color is its own
    // product with a size selector; the variant UI is size-based). Match the SIZE token wherever it
    // sits in the name, longest form first so "3XL"/"2X Large" win over "L"/"Large".
    // Handles: 2XL–5XL, XXL/XXXL, "2X Large".."5X Large", X-Small/X-Large, Small/Medium/Large/
    // Extra Large, XS/S/M/L/XL, and measured sizes (12 oz, 3 in, etc.).
    const unitMatch = name.match(/(\d+(?:\.\d+)?\s*(?:oz|ounce|ml|liter|inch|in|cm|mm)\.?)/i);
    const clothingSizeMatch = name.match(/\b([2-5]\s?X\s?Large|[2-5]\s?XL|XXXL|XXL|Extra\s?Large|X[-\s]?Small|X[-\s]?Large|Small|Medium|Large|XS|XL|S|M|L)\b/i);

    let size = 'One Size';
    let baseName = name;
    if (unitMatch) {
        size = unitMatch[1].replace(/\.$/, '').replace(/\s+/g, ' ').trim();
        baseName = name.replace(unitMatch[0], ' ');
    } else if (clothingSizeMatch) {
        size = clothingSizeMatch[1].replace(/\s+/g, ' ').trim();
        // remove the size token (and a trailing "large" already captured) from the base
        baseName = name.replace(clothingSizeMatch[0], ' ');
    }

    baseName = baseName.replace(/\s+/g, ' ').trim();

    const result = { size, baseName };
    _variantCache.set(name, result);
    return result;
}

// Collapse a product array into one entry per base product (grouped by baseName + price), preserving
// first-occurrence order. Each entry keeps all original product fields plus: baseName, variants[],
// hasVariants; when grouped it uses the baseName as the display name.
export function groupProductVariants(products) {
    const grouped = {};

    (products || []).forEach(product => {
        const { size: extractedSize, baseName } = parseProductVariant(product.name);
        const key = `${baseName}_${product.price || 0}`;

        if (!grouped[key]) {
            grouped[key] = {
                ...product,
                baseName: baseName,
                variants: [],
                hasVariants: false
            };
        }

        grouped[key].variants.push({
            ...product,
            size: extractedSize,
            originalName: product.name,
            available: product.available !== false
        });

        if (grouped[key].variants.length > 1) {
            grouped[key].hasVariants = true;
            grouped[key].name = baseName; // one card, base name
        }
    });

    return Object.values(grouped);
}
